const AURA_VIDEO_REF_RE = /^[A-Za-z0-9_-]{1,64}$/;

const AURA_VIDEO_PATH_PATTERNS: readonly RegExp[] = [
  /^\/api\/link\/video\/([^/?#]+)\/?$/,
  /^\/link\/video\/([^/?#]+)\/?$/,
  /^\/video\/([^/?#]+)\/?$/,
  /^\/api\/embed\/([^/?#]+)\/?$/,
];

export type ParsedAuraVideoUrl = {
  videoRef: string;
  /** `https://<host>` of the pasted URL; only set once the host passed the allowlist. */
  origin: string;
};

export function parseAuraVideoRefFromUrl(
  rawUrl: string,
  embedOrigin: string | undefined,
  pasteAllowedHostnames?: readonly string[] | undefined
): string | null {
  return parseAuraVideoUrl(rawUrl, embedOrigin, pasteAllowedHostnames)?.videoRef ?? null;
}

/**
 * Like `parseAuraVideoRefFromUrl`, but also returns the (allowlisted) origin the link was pasted
 * from. Video refs are only valid on the Aura deployment that issued them, so a prod link pasted
 * into stage must keep pointing at prod Aura rather than the environment's default Aura host.
 */
export function parseAuraVideoUrl(
  rawUrl: string,
  embedOrigin: string | undefined,
  pasteAllowedHostnames?: readonly string[] | undefined
): ParsedAuraVideoUrl | null {
  if (!embedOrigin) {
    return null;
  }

  let embedHost: string;
  try {
    embedHost = new URL(embedOrigin).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (!embedHost) {
    return null;
  }

  const pasteHostSet =
    pasteAllowedHostnames && pasteAllowedHostnames.length > 0
      ? new Set(pasteAllowedHostnames.map((h) => h.toLowerCase()))
      : new Set([embedHost]);

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return null;
  }
  if (parsed.username || parsed.password) {
    return null;
  }
  if (!pasteHostSet.has(parsed.hostname.toLowerCase())) {
    return null;
  }

  for (const pattern of AURA_VIDEO_PATH_PATTERNS) {
    const match = pattern.exec(parsed.pathname);
    if (!match) {
      continue;
    }
    let ref: string;
    try {
      ref = decodeURIComponent(match[1]);
    } catch {
      return null;
    }
    return AURA_VIDEO_REF_RE.test(ref)
      ? {videoRef: ref, origin: `https://${parsed.hostname.toLowerCase()}`}
      : null;
  }

  return null;
}

/**
 * Returns `origin` normalized to `https://<host>` when its host is allowlisted, else `fallback`.
 * Stored node origins come from saved documents, so they are re-validated before use.
 */
export function resolveAuraEmbedOrigin(
  origin: string | undefined,
  allowedHostnames: readonly string[],
  fallback: string | undefined
): string | undefined {
  if (!origin) {
    return fallback;
  }
  let parsed: URL;
  try {
    parsed = new URL(origin);
  } catch {
    return fallback;
  }
  const host = parsed.hostname.toLowerCase();
  const allowed = allowedHostnames.some((h) => h.toLowerCase() === host);
  if (!allowed || parsed.protocol !== 'https:' || parsed.username || parsed.password) {
    return fallback;
  }
  return `https://${host}`;
}

export function buildAuraEmbedSrc(
  videoRef: string,
  embedOrigin: string | undefined
): string | null {
  if (typeof videoRef !== 'string' || !embedOrigin || !AURA_VIDEO_REF_RE.test(videoRef)) {
    return null;
  }
  return `${embedOrigin.replace(/\/+$/, '')}/api/embed/${encodeURIComponent(videoRef)}`;
}
